import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isNil } from 'lodash';
import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastModule } from 'primeng/toast';
import { firstValueFrom } from 'rxjs';

import {
  displayTime,
  EventConfig,
  EventConfigSchema,
  EventData,
  EventDataSchema,
  Interval,
  OrgData,
  UserData,
} from '@pwp-common';

import { provideValidator } from '../../../../common/form/provide-validator';
import { provideValueAccessor } from '../../../../common/form/provide-value-accessor';
import { MenuOption } from '../../../../common/form/typings/menu-options';
import {
  DATETIME_LOCAL_CONTROL_STR_FORMAT,
  getTimestampFromDateTimeLocalFieldControl,
} from '../../../../common/objects/form-helper';
import { userDataDisplayFormat } from '../../../../common/objects/user-data-display-format';
import { DatetimeValidator } from '../../../../common/validators/datetime-validator/datetime-validator';
import { isBeforeControl } from '../../../../common/validators/datetime-validator/is-before-control/is-before-control';
import { EventConfigService } from '../../../../services/event/event-config/event-config.service';
import { EventsService } from '../../../../services/event/events/events.service';
import { RruleEditorComponent } from '../../../core/rrule-editor/rrule-editor.component';
import { FormFieldHelpComponent } from '../../../form/form-field-help/form-field-help.component';
import { FormGroupControlValueAccessor } from '../../../generic/abstract-classes/form-group-control-value-accessor';
import { ConfirmWithInputComponent } from '../../../generic/confirm-with-input/confirm-with-input.component';
import { SettingsModule } from '../../../generic/settings/settings.module';

import { dbToEventCreateDialogEditorOutput } from './editor-output/db-to-event-create-dialog-editor-output/db-to-event-create-dialog-editor-output';
import { EventCreateDialogEditorOutput } from './editor-output/event-create-dialog-editor-output';
import { eventCreateDialogEditorOutputToDb } from './editor-output/event-create-dialog-editor-output-to-db/event-create-dialog-editor-output-to-db';
import { EventCreateDialogFormType } from './editor-output/event-create-dialog-form-type';
import { formToEventCreateDialogEditorOutput } from './editor-output/form-to-event-create-dialog-editor-output/form-to-event-create-dialog-editor-output';
import { EventDialogMode, RepeatEnd } from './enums';

@UntilDestroy()
@Component({
  selector: 'app-event-create-dialog',
  standalone: true,
  imports: [
    CheckboxModule,
    CommonModule,
    DropdownModule,
    FormFieldHelpComponent,
    InputTextModule,
    MatIconModule,
    RadioButtonModule,
    ReactiveFormsModule,
    RruleEditorComponent,
    SettingsModule,
    ToastModule,
    TranslocoModule,
  ],
  templateUrl: './event-create-dialog.component.html',
  styleUrls: ['./event-create-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideValueAccessor(() => EventCreateDialogComponent),
    provideValidator(() => EventCreateDialogComponent),
    MessageService,
  ],
})
export class EventCreateDialogComponent
  extends FormGroupControlValueAccessor<EventCreateDialogFormType, EventCreateDialogEditorOutput>
  implements OnInit
{
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Dependencies
  /////////////////////////////////////////////////////////////////////////////////////////////////////////

  private readonly dialogRef = inject(DynamicDialogRef);

  private readonly dialogConfig = inject(DynamicDialogConfig);

  private readonly eventsService = inject(EventsService);

  private readonly eventConfigService = inject(EventConfigService);

  private readonly translocoService = inject(TranslocoService);

  private readonly messageService = inject(MessageService);

  private readonly dialogService = inject(DialogService);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////////////////////////////////

  private start!: moment.Moment;

  private end!: moment.Moment;

  private type!: string;

  private orgData!: OrgData;

  private mode: EventDialogMode = EventDialogMode.Create;

  private selectedEvent?: EventData;

  private oldEventConfig?: EventConfig;

  public userSelectOptions: MenuOption<string>[] = [];

  public hasOverlap = false;

  public overlappingEvent: EventData | undefined;

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////////////////

  public override ngOnInit() {
    super.ngOnInit();

    const data = this.dialogConfig?.data;
    this.start = data?.start;
    this.end = data?.end;
    this.type = data?.type;
    this.orgData = data?.orgData;
    this.mode = data?.mode ?? EventDialogMode.Create;
    this.selectedEvent = data?.eventData;
    this.oldEventConfig = data?.eventConfig;
    const userDataArray: UserData[] = data?.userDataArray ?? [];

    this.userSelectOptions = this.createUserSelectOptions(userDataArray);

    const timezone = this.orgData?.getTimezone();
    const initialEditorOutput = this.createInitialEditorOutput(timezone);
    this.writeValue(initialEditorOutput);

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.checkOverlap().catch((error) => console.error('Error checking overlap:', error));
    });

    this.isRepeating.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.handleIsRepeatingChange(value);
    });

    this.repeatEnd.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.handleRepeatEndChange(value);
    });

    if (this.mode === EventDialogMode.EditRecurring) {
      this.handleIsRepeatingChange(true);
      this.handleRepeatEndChange(RepeatEnd.continueIndefinitely);
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Public Methods
  /////////////////////////////////////////////////////////////////////////////////////////////

  public defineForm() {
    this.form = new UntypedFormGroup(
      {
        startTime: new UntypedFormControl('', [Validators.required, DatetimeValidator.stepSizeSeconds(900)]),
        endTime: new UntypedFormControl('', [Validators.required, DatetimeValidator.stepSizeSeconds(900)]),
        isRepeating: new UntypedFormControl(false),
        rrule: new UntypedFormControl(''),
        repeatEnd: new UntypedFormControl(''),
        repeatEndDate: new UntypedFormControl(''),
        extendScheduleNumDays: new UntypedFormControl(90),
        assignedUserId: new UntypedFormControl(''),
        assignedBackupUserId: new UntypedFormControl(''),
      },
      {
        validators: [
          isBeforeControl('startTime', 'endTime'),
          EventCreateDialogComponent.validateRepeatEndDateAfterEndTime,
        ],
      },
    );
  }

  public override writeValue(value: EventCreateDialogEditorOutput) {
    if (isNil(value)) {
      return;
    }

    this.form.patchValue(
      {
        startTime: value.startTime ?? '',
        endTime: value.endTime ?? '',
        isRepeating: value.isRepeating ?? false,
        rrule: value.rrule ?? '',
        repeatEnd: value.repeatEnd ?? '',
        repeatEndDate: value.repeatEndDate ?? '',
        extendScheduleNumDays: value.extendScheduleNumDays ?? 90,
        assignedUserId: value.assignedUserId ?? '',
        assignedBackupUserId: value.assignedBackupUserId ?? '',
      },
      { emitEvent: false },
    );

    this.changeDetectorRef.detectChanges();
  }

  public resetForm = () => {
    const timezone = this.orgData.getTimezone();
    const initialEditorOutput = this.createInitialEditorOutput(timezone);

    this.writeValue(initialEditorOutput);
    this.hasOverlap = false;
    this.overlappingEvent = undefined;

    const isRepeating = initialEditorOutput.isRepeating ?? false;
    const repeatEnd = initialEditorOutput.repeatEnd ?? RepeatEnd.none;
    this.handleIsRepeatingChange(isRepeating);
    this.handleRepeatEndChange(repeatEnd);
  };

  public getOverlapErrorMessage(): string {
    if (!this.hasOverlap || isNil(this.overlappingEvent)) {
      return '';
    }
    const timezone = this.orgData.getTimezone();
    return this.translocoService.translate('event-create-dialog.overlapWarning', {
      start: displayTime(this.overlappingEvent.getStart(), timezone, { timeOnly: false }),
      end: displayTime(this.overlappingEvent.getEnd(), timezone, { timeOnly: false }),
    });
  }

  public shouldShowRepeatSection(): boolean {
    return this.mode !== EventDialogMode.EditNonRecurring;
  }

  public shouldShowRepeatEndSection(): boolean {
    return this.isRepeating?.value === true;
  }

  public shouldShowRepeatEndDatePicker(): boolean {
    return this.repeatEnd?.value === RepeatEnd.endDate;
  }

  public shouldShowExtendScheduleNumDays(): boolean {
    return this.repeatEnd?.value === RepeatEnd.continueIndefinitely;
  }

  // eslint-disable-next-line class-methods-use-this
  public parseValueChange(value: EventCreateDialogFormType): EventCreateDialogEditorOutput {
    return formToEventCreateDialogEditorOutput(value);
  }

  protected override makeValidationErrors(): ValidationErrors {
    return { 'event-create-dialog': this.form.value };
  }

  public isValid = () => {
    if (this.form?.invalid || this.hasOverlap) {
      return false;
    }

    return true;
  };

  // eslint-disable-next-line class-methods-use-this
  public refreshData = async (): Promise<void> => undefined;

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Private Methods
  /////////////////////////////////////////////////////////////////////////////////////////////

  private createInitialEditorOutput(timezone: string): EventCreateDialogEditorOutput {
    if (this.mode === EventDialogMode.EditNonRecurring) {
      return dbToEventCreateDialogEditorOutput({ eventData: this.selectedEvent, timezone });
    }

    if (this.mode === EventDialogMode.EditRecurring) {
      return {
        startTime: this.selectedEvent?.getStart().tz(timezone).format(DATETIME_LOCAL_CONTROL_STR_FORMAT) ?? '',
        endTime: this.selectedEvent?.getEnd().tz(timezone).format(DATETIME_LOCAL_CONTROL_STR_FORMAT) ?? '',
        isRepeating: true,
        rrule: this.oldEventConfig?.getRRule() ?? '',
        repeatEnd: RepeatEnd.continueIndefinitely,
        repeatEndDate: '',
        extendScheduleNumDays: this.oldEventConfig?.getExtendScheduleNumDays() ?? 90,
        assignedUserId: this.selectedEvent?.getAssignedUserId() ?? '',
        assignedBackupUserId: this.selectedEvent?.getAssignedBackupUserId() ?? '',
      };
    }

    return {
      startTime: this.start?.tz(timezone).format(DATETIME_LOCAL_CONTROL_STR_FORMAT) ?? '',
      endTime: this.end?.tz(timezone).format(DATETIME_LOCAL_CONTROL_STR_FORMAT) ?? '',
      isRepeating: false,
      rrule: '',
      repeatEnd: RepeatEnd.none,
      repeatEndDate: '',
      extendScheduleNumDays: 90,
      assignedUserId: '',
      assignedBackupUserId: '',
    };
  }

  private createUserSelectOptions(userDataArray: UserData[]): MenuOption<string>[] {
    const userList = userDataArray.map((user) => ({
      label: userDataDisplayFormat(user),
      value: user.getId(),
    }));

    const unassignedOption = {
      label: this.translocoService.translate('event-create-dialog.unassignedOption'),
      value: '',
    };
    return [unassignedOption, ...userList];
  }

  private handleIsRepeatingChange(isRepeating: boolean) {
    if (!isRepeating) {
      this.repeatEnd.setValue(RepeatEnd.none, { emitEvent: false });
      this.repeatEndDate.setValue('', { emitEvent: false });
      this.repeatEndDate.setValidators([]);
      this.rrule.setValidators([]);
    } else {
      this.rrule.setValidators([Validators.required]);
    }

    const validators = isRepeating ? [Validators.required] : [];
    this.repeatEnd.setValidators(validators);
    this.repeatEnd.updateValueAndValidity();
    this.repeatEndDate.updateValueAndValidity();
    this.rrule.updateValueAndValidity();
    this.changeDetectorRef.detectChanges();
  }

  private handleRepeatEndChange(repeatEndValue: string) {
    if (repeatEndValue === RepeatEnd.endDate) {
      this.repeatEndDate.setValidators([Validators.required, DatetimeValidator.stepSizeSeconds(900)]);
      this.extendScheduleNumDays.setValidators([]);
      this.extendScheduleNumDays.setValue(90, { emitEvent: false });
      this.repeatEndDate.setValue(this.endTime.value, { emitEvent: false });
    } else if (repeatEndValue === RepeatEnd.continueIndefinitely) {
      this.repeatEndDate.setValidators([]);
      this.repeatEndDate.setValue('', { emitEvent: false });
      this.extendScheduleNumDays.setValidators([Validators.required, Validators.min(1), Validators.max(365)]);
    } else {
      this.repeatEndDate.setValidators([]);
      this.repeatEndDate.setValue('', { emitEvent: false });
      this.extendScheduleNumDays.setValidators([]);
      this.extendScheduleNumDays.setValue(90, { emitEvent: false });
    }

    this.repeatEndDate.updateValueAndValidity();
    this.extendScheduleNumDays.updateValueAndValidity();
    this.form.updateValueAndValidity();
    this.changeDetectorRef.detectChanges();
  }

  private static validateRepeatEndDateAfterEndTime(control: AbstractControl): ValidationErrors | null {
    const formGroup = control as UntypedFormGroup;
    const repeatEnd = formGroup.get('repeatEnd') as UntypedFormControl;
    const endTime = formGroup.get('endTime') as UntypedFormControl;
    const repeatEndDate = formGroup.get('repeatEndDate') as UntypedFormControl;

    if (repeatEnd?.value !== RepeatEnd.endDate) {
      return null;
    }

    if (!endTime?.value || !repeatEndDate?.value) {
      return null;
    }

    const formattedEndTime = moment(endTime.value);
    const formattedRepeatEndDate = moment(repeatEndDate.value);

    if (formattedRepeatEndDate.isBefore(formattedEndTime)) {
      return { repeatEndDateMustBeAfterEndTime: true };
    }

    return null;
  }

  private static extractRRule(fullRruleString: string): string {
    if (isNil(fullRruleString) || fullRruleString === '') {
      return '';
    }
    const rruleString = fullRruleString
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line.startsWith('RRULE:'));

    return rruleString ?? '';
  }

  private async checkOverlap(): Promise<void> {
    const timezone = this.orgData?.getTimezone();
    if (isNil(timezone)) {
      return;
    }

    const startTime = getTimestampFromDateTimeLocalFieldControl(this.startTime, timezone, undefined);
    const endTime = getTimestampFromDateTimeLocalFieldControl(this.endTime, timezone, undefined);

    if (isNil(startTime) || isNil(endTime) || !endTime.isAfter(startTime)) {
      this.hasOverlap = false;
      this.overlappingEvent = undefined;
      this.changeDetectorRef.detectChanges();
      return;
    }

    try {
      const existingEvents = await firstValueFrom(
        this.eventsService.getEventsWithStartOrEndInRange(startTime, endTime, true, this.type),
      );

      const selectedEventId = this.selectedEvent?.getId();
      const newEventInterval = new Interval(startTime, endTime);
      for (const event of existingEvents) {
        const isCurrentEvent = selectedEventId && event.getId() === selectedEventId;
        if (!isCurrentEvent && event.getIntervalMs().overlaps(newEventInterval)) {
          this.hasOverlap = true;
          this.overlappingEvent = event;
          this.changeDetectorRef.detectChanges();
          return;
        }
      }

      this.hasOverlap = false;
      this.overlappingEvent = undefined;
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      console.error('Error checking overlap:', error);
      this.hasOverlap = false;
      this.overlappingEvent = undefined;
      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Generates events.
   */
  private generateEvents(): EventData[] {
    const timezone = this.orgData.getTimezone();

    const startTime = getTimestampFromDateTimeLocalFieldControl(this.startTime, timezone, undefined);
    const endTime = getTimestampFromDateTimeLocalFieldControl(this.endTime, timezone, undefined);

    if (isNil(startTime) || isNil(endTime)) {
      return [];
    }

    const isRepeating = this.isRepeating?.value ?? false;
    const assignedUserId = this.assignedUserId?.value ?? '';
    const assignedBackupUserId = this.assignedBackupUserId?.value ?? '';

    if (!isRepeating) {
      return [
        new EventData({
          id: '',
          start: startTime,
          end: endTime,
          type: this.type,
          color: EventDataSchema.Defaults.color,
          assignedUserId: assignedUserId ?? EventDataSchema.Defaults.assignedUserId,
          assignedBackupUserId: assignedBackupUserId ?? EventDataSchema.Defaults.assignedBackupUserId,
          eventConfigId: EventDataSchema.Defaults.eventConfigId,
        }),
      ];
    }

    const repeatEndDate = getTimestampFromDateTimeLocalFieldControl(this.repeatEndDate, timezone, undefined);
    const rruleString = EventCreateDialogComponent.extractRRule(this.rrule?.value ?? '');

    const eventConfig = new EventConfig({
      id: '',
      start: startTime,
      end: endTime,
      rrule: rruleString,
      generate: true,
      type: this.type,
      color: EventConfigSchema.Defaults.color,
      assignedUserId: assignedUserId ?? EventConfigSchema.Defaults.assignedUserId,
      assignedBackupUserId: assignedBackupUserId ?? EventConfigSchema.Defaults.assignedBackupUserId,
      displayName: EventConfigSchema.Defaults.displayName,
      description: EventConfigSchema.Defaults.description,
    });

    const events = eventConfig.generateEventsWithStartBetween(startTime, repeatEndDate!, timezone);

    return events;
  }

  /**
   * Validates that the generated events don't overlap with existing events in the database.
   */
  private async validateNoOverlaps(events: EventData[]): Promise<boolean> {
    if (events.length === 0) {
      return false;
    }

    const minStart = moment.min(events.map((event) => event.getStart()));
    const maxEnd = moment.max(events.map((event) => event.getEnd()));

    const existingEvents = await firstValueFrom(
      this.eventsService.getEventsWithStartInRange(minStart, maxEnd, this.type),
    );

    for (const existingEvent of existingEvents) {
      for (const newEvent of events) {
        if (existingEvent.getIntervalMs().overlaps(newEvent.getIntervalMs())) {
          this.showErrorExistingOverlap(existingEvent);
          return false;
        }
      }
    }

    for (let i = 0; i < events.length; i += 1) {
      for (let j = i + 1; j < events.length; j += 1) {
        if (events[i].getIntervalMs().overlaps(events[j].getIntervalMs())) {
          this.showErrorNewEventsOverlap(events[i], events[j]);
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Shows error message when a new event overlaps with an existing event in the database.
   */
  private showErrorExistingOverlap(existingEvent: EventData) {
    const timezone = this.orgData.getTimezone();
    const message = this.translocoService.translate('event-create-dialog.failureAlreadyExists', {
      start: displayTime(existingEvent.getStart(), timezone, { timeOnly: false }),
      end: displayTime(existingEvent.getEnd(), timezone, { timeOnly: false }),
    });

    this.messageService.add({
      severity: 'error',
      summary: message,
    });
  }

  /**
   * Shows error message when two newly generated events overlap with each other.
   */
  private showErrorNewEventsOverlap(event1: EventData, event2: EventData) {
    const timezone = this.orgData.getTimezone();
    const message = this.translocoService.translate('event-create-dialog.failureNewEventsOverlap', {
      start1: displayTime(event1.getStart(), timezone, { timeOnly: false }),
      end1: displayTime(event1.getEnd(), timezone, { timeOnly: false }),
      start2: displayTime(event2.getStart(), timezone, { timeOnly: false }),
      end2: displayTime(event2.getEnd(), timezone, { timeOnly: false }),
    });

    this.messageService.add({
      severity: 'error',
      summary: message,
    });
  }

  private showSuccessMessage() {
    const key =
      this.mode === EventDialogMode.EditNonRecurring
        ? 'event-create-dialog.editSuccess'
        : 'event-create-dialog.success';
    const message = this.translocoService.translate(key);
    this.messageService.add({
      severity: 'success',
      summary: message,
    });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Form Controls
  /////////////////////////////////////////////////////////////////////////////////////////////

  public get startTime() {
    return this.form.get('startTime') as UntypedFormControl;
  }

  public get endTime() {
    return this.form.get('endTime') as UntypedFormControl;
  }

  public get isRepeating() {
    return this.form.get('isRepeating') as UntypedFormControl;
  }

  public get rrule() {
    return this.form.get('rrule') as UntypedFormControl;
  }

  public get repeatEnd() {
    return this.form.get('repeatEnd') as UntypedFormControl;
  }

  public get repeatEndDate() {
    return this.form.get('repeatEndDate') as UntypedFormControl;
  }

  public get extendScheduleNumDays() {
    return this.form.get('extendScheduleNumDays') as UntypedFormControl;
  }

  public get assignedUserId() {
    return this.form.get('assignedUserId') as UntypedFormControl;
  }

  public get assignedBackupUserId() {
    return this.form.get('assignedBackupUserId') as UntypedFormControl;
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Upload
  /////////////////////////////////////////////////////////////////////////////////////////////

  public upload = async (): Promise<void> => {
    if (!this.isValid()) {
      return;
    }

    this.dialogConfig.closable = false;
    this.changeDetectorRef.detectChanges();

    let uploadFailed = false;

    try {
      if (this.mode === EventDialogMode.EditNonRecurring) {
        await this.updateNonRecurringEvent();
        this.dialogRef.close(true);
      } else if (this.mode === EventDialogMode.EditRecurring) {
        const succeeded = await this.updateRecurringSchedule();
        if (succeeded) {
          this.dialogRef.close(true);
        }
      } else {
        const repeatEndValue = this.repeatEnd?.value;

        if (repeatEndValue === RepeatEnd.continueIndefinitely) {
          await this.uploadEventConfig();
          this.showSuccessMessage();
          this.dialogRef.close(true);
        } else {
          const uploaded = await this.uploadEvents();
          if (uploaded) {
            this.showSuccessMessage();
            this.dialogRef.close(true);
          } else {
            uploadFailed = true;
          }
        }
      }
    } catch (error) {
      console.error('Error uploading events:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('event-create-dialog.uploadError'),
      });
      uploadFailed = true;
    } finally {
      this.dialogConfig.closable = true;
      this.changeDetectorRef.detectChanges();
    }

    if (uploadFailed) {
      throw new Error();
    }
  };

  private async updateNonRecurringEvent(): Promise<void> {
    if (!this.selectedEvent) {
      throw new Error('No editing event data available');
    }

    const editorOutput = formToEventCreateDialogEditorOutput(this.form.value);
    const updatedEvent = eventCreateDialogEditorOutputToDb({
      editorOutput,
      type: this.selectedEvent.getType(),
      timezone: this.orgData.getTimezone(),
      id: this.selectedEvent.getId(),
      color: this.selectedEvent.getColor(),
      eventConfigId: this.selectedEvent.getEventConfigId(),
    });

    await firstValueFrom(this.eventsService.upload({ obj: updatedEvent, mode: 'update' }));
  }

  private async uploadEvents(): Promise<boolean> {
    const events = this.generateEvents();

    const isValidOverlap = await this.validateNoOverlaps(events);
    if (!isValidOverlap) {
      return false;
    }

    await firstValueFrom(this.eventsService.uploadArray(events, 'create', true));
    return true;
  }

  private async uploadEventConfig(): Promise<void> {
    const timezone = this.orgData.getTimezone();
    const startTime = getTimestampFromDateTimeLocalFieldControl(this.startTime, timezone, undefined);
    const endTime = getTimestampFromDateTimeLocalFieldControl(this.endTime, timezone, undefined);

    if (isNil(startTime) || isNil(endTime)) {
      throw new Error('Start time and end time are required');
    }

    const rruleString = EventCreateDialogComponent.extractRRule(this.rrule?.value ?? '');
    const assignedUserId = this.assignedUserId?.value ?? '';
    const assignedBackupUserId = this.assignedBackupUserId?.value ?? '';
    const extendScheduleNumDays = this.extendScheduleNumDays?.value ?? 90;

    const eventConfig = new EventConfig({
      id: '',
      start: startTime,
      end: endTime,
      rrule: rruleString,
      generate: true,
      extendScheduleNumDays,
      type: this.type,
      color: EventConfigSchema.Defaults.color,
      assignedUserId: assignedUserId ?? EventConfigSchema.Defaults.assignedUserId,
      assignedBackupUserId: assignedBackupUserId ?? EventConfigSchema.Defaults.assignedBackupUserId,
      displayName: EventConfigSchema.Defaults.displayName,
      description: EventConfigSchema.Defaults.description,
    });

    await firstValueFrom(this.eventConfigService.upload({ obj: eventConfig, mode: 'create', generateId: true }));
  }

  private async updateRecurringSchedule(): Promise<boolean> {
    const confirmed = await this.confirmEditRecurringSchedule();
    if (!confirmed) {
      return false;
    }

    const isRepeating = this.isRepeating?.value ?? false;
    const repeatEndValue = this.repeatEnd?.value;

    await this.deleteAllFollowingShifts();

    if (isRepeating && repeatEndValue === RepeatEnd.continueIndefinitely) {
      await this.updateExistingEventConfig();
      return true;
    }

    await this.stopOldEventConfig();
    return this.uploadEvents();
  }

  private async updateExistingEventConfig(): Promise<void> {
    if (!this.oldEventConfig) {
      throw new Error('Missing event config for recurring edit');
    }

    const timezone = this.orgData.getTimezone();
    const startTime = getTimestampFromDateTimeLocalFieldControl(this.startTime, timezone, undefined);
    const endTime = getTimestampFromDateTimeLocalFieldControl(this.endTime, timezone, undefined);

    if (isNil(startTime) || isNil(endTime)) {
      throw new Error('Start time and end time are required');
    }

    const rruleString = EventCreateDialogComponent.extractRRule(this.rrule?.value ?? '');
    const assignedUserId = this.assignedUserId?.value ?? '';
    const assignedBackupUserId = this.assignedBackupUserId?.value ?? '';
    const extendScheduleNumDays = this.extendScheduleNumDays?.value ?? 90;

    const updatedConfig = new EventConfig({
      id: this.oldEventConfig.getId(),
      start: startTime,
      end: endTime,
      rrule: rruleString,
      generate: true,
      extendScheduleNumDays,
      type: this.oldEventConfig.getType(),
      color: this.oldEventConfig.getColor(),
      assignedUserId: assignedUserId ?? EventConfigSchema.Defaults.assignedUserId,
      assignedBackupUserId: assignedBackupUserId ?? EventConfigSchema.Defaults.assignedBackupUserId,
      displayName: this.oldEventConfig.getDisplayName(),
      description: this.oldEventConfig.getDescription(),
    });

    await firstValueFrom(this.eventConfigService.upload({ obj: updatedConfig, mode: 'update' }));
  }

  private async stopOldEventConfig(): Promise<void> {
    if (!this.oldEventConfig) {
      throw new Error('Missing event config for recurring edit');
    }

    const timezone = this.orgData.getTimezone();
    const newEventConfig = new EventConfig({
      id: this.oldEventConfig.getId(),
      start: this.oldEventConfig.getStart(timezone),
      end: this.oldEventConfig.getEnd(timezone),
      rrule: this.oldEventConfig.getRRule(),
      generate: false,
      type: this.oldEventConfig.getType(),
      color: this.oldEventConfig.getColor(),
      assignedUserId: this.oldEventConfig.getAssignedUserId(),
      assignedBackupUserId: this.oldEventConfig.getAssignedBackupUserId(),
      displayName: this.oldEventConfig.getDisplayName(),
      description: this.oldEventConfig.getDescription(),
      extendScheduleNumDays: this.oldEventConfig.getExtendScheduleNumDays(),
    });

    await firstValueFrom(this.eventConfigService.upload({ obj: newEventConfig, mode: 'update' }));
  }

  private async confirmEditRecurringSchedule(): Promise<boolean> {
    if (!this.oldEventConfig || !this.selectedEvent) {
      return false;
    }

    const eventConfigId = this.oldEventConfig.getId();
    const selectedShiftStart = this.selectedEvent.getStart();

    const allEvents = await firstValueFrom(
      this.eventsService.getDocsArray({
        query: [{ fieldPath: EventDataSchema.eventConfigId, opStr: '==', value: eventConfigId }],
      }),
    );

    const followingShifts = allEvents.filter((event) => event.getStart().isSameOrAfter(selectedShiftStart));

    const assignedCount = followingShifts.filter(
      (event) => event.getAssignedUserId() || event.getAssignedBackupUserId(),
    ).length;
    const unassignedCount = followingShifts.length - assignedCount;
    const totalCount = followingShifts.length;

    const title = this.translocoService.translate('event-create-dialog.editRecurringConfirmTitle');
    const body = this.translocoService.translate('event-create-dialog.editRecurringConfirmBody', {
      total: totalCount,
      unassigned: unassignedCount,
      assigned: assignedCount,
    });

    const result = await firstValueFrom(
      this.dialogService.open(ConfirmWithInputComponent, {
        header: title,
        width: '30%',
        data: {
          title,
          body,
          requiredConfirmationString: String(totalCount),
        },
      }).onClose,
    );

    return !!result;
  }

  private async deleteAllFollowingShifts(): Promise<void> {
    if (!this.oldEventConfig || !this.selectedEvent) {
      throw new Error('Missing event config or selected event for recurring edit');
    }

    const eventConfigId = this.oldEventConfig.getId();
    const selectedShiftStart = this.selectedEvent.getStart();

    const allEvents = await firstValueFrom(
      this.eventsService.getDocsArray({
        query: [{ fieldPath: EventDataSchema.eventConfigId, opStr: '==', value: eventConfigId }],
      }),
    );

    const followingShifts = allEvents.filter((event) => event.getStart().isSameOrAfter(selectedShiftStart));

    if (followingShifts.length > 0) {
      const eventIds = followingShifts.map((event) => event.getId());
      await this.eventsService.deleteDocs(eventIds);
    }
  }
}
